import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, IconButton, TextField, Box, Alert, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Typography, Tooltip
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE } from '../../apiBase';

export default function OrganizationApiKeysDialog({ open, onClose, organization }) {
    const [configuredKeys, setConfiguredKeys] = useState({});
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    
    // Add/Edit key states
    const [showAddKey, setShowAddKey] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [apiKeyValue, setApiKeyValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(null);

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    };

    useEffect(() => {
        if (open && organization) {
            fetchData();
        }
    }, [open, organization]);

    const fetchData = async () => {
        setLoading(true);
        setMessage('');
        
        try {
            // Fetch available providers from LLM registry
            const providersRes = await fetch(`${API_BASE}/admin/llm-providers`, { headers });
            if (providersRes.ok) {
                const providersData = await providersRes.json();
                // Only show enabled providers
                setProviders(providersData.filter(p => p.is_enabled));
            }

            // Fetch configured API keys for this organization
            const keysRes = await fetch(`${API_BASE}/organizations/${organization.id}/api-keys`, { headers });
            if (keysRes.ok) {
                const keysData = await keysRes.json();
                setConfiguredKeys(keysData.configured_providers || {});
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveApiKey = async () => {
        if (!selectedProvider || !apiKeyValue) {
            setMessage('Please select a provider and enter an API key');
            return;
        }

        setSaving(true);
        setMessage('');

        try {
            const res = await fetch(`${API_BASE}/organizations/${organization.id}/api-keys`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    provider: selectedProvider,
                    api_key: apiKeyValue
                })
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to save API key');
            }

            setMessage(`✅ API key for ${selectedProvider} saved successfully`);
            setShowAddKey(false);
            setSelectedProvider('');
            setApiKeyValue('');

            // Refresh configured keys
            await fetchData();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteApiKey = async (provider) => {
        if (!window.confirm(`Delete API key for ${provider}?`)) return;

        setMessage('');
        try {
            const res = await fetch(`${API_BASE}/organizations/${organization.id}/api-keys/${provider}`, {
                method: 'DELETE',
                headers
            });

            if (!res.ok) throw new Error('Failed to delete API key');

            setMessage(`✅ API key for ${provider} deleted`);
            
            // Update local state
            setConfiguredKeys(prev => {
                const updated = { ...prev };
                delete updated[provider];
                return updated;
            });
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleValidateApiKey = async (provider) => {
        setValidating(provider);
        try {
            // This would call a backend endpoint to validate the key
            // For now, we'll just show a placeholder
            await new Promise(resolve => setTimeout(resolve, 1000));
            setMessage(`✅ ${provider} API key validated successfully`);
        } catch (err) {
            setMessage(`❌ Failed to validate: ${err.message}`);
        } finally {
            setValidating(null);
        }
    };

    const getProviderDisplayName = (providerName) => {
        const provider = providers.find(p => p.name === providerName);
        return provider ? provider.display_name : providerName;
    };

    const getUnconfiguredProviders = () => {
        return providers.filter(p => !configuredKeys[p.name]);
    };

    if (!organization) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage API Keys: {organization.name}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        {message && (
                            <Alert
                                severity={message.startsWith('✅') ? 'success' : 'error'}
                                onClose={() => setMessage('')}
                            >
                                {message}
                            </Alert>
                        )}

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Configured API Keys</Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setShowAddKey(true)}
                                disabled={getUnconfiguredProviders().length === 0}
                            >
                                Add Key
                            </Button>
                        </Box>

                        {Object.keys(configuredKeys).length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No API keys configured for this organization
                                </Typography>
                            </Paper>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Provider</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Models Available</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(configuredKeys).map(([providerName, config]) => {
                                            const provider = providers.find(p => p.name === providerName);
                                            const modelCount = provider ? provider.model_count : 0;
                                            
                                            return (
                                                <TableRow key={providerName}>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <VpnKeyIcon fontSize="small" color="action" />
                                                            <Typography variant="body2">
                                                                {getProviderDisplayName(providerName)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={<CheckCircleIcon />}
                                                            label="Configured"
                                                            size="small"
                                                            color="success"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={`${modelCount} models`} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Validate Key">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleValidateApiKey(providerName)}
                                                                disabled={validating === providerName}
                                                            >
                                                                {validating === providerName ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CheckCircleIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteApiKey(providerName)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Add Key Section */}
                        {showAddKey && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" mb={2}>Add New API Key</Typography>
                                <Box display="grid" gap={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Provider</InputLabel>
                                        <Select
                                            value={selectedProvider}
                                            onChange={(e) => setSelectedProvider(e.target.value)}
                                            label="Provider"
                                        >
                                            {getUnconfiguredProviders().map(p => (
                                                <MenuItem key={p.name} value={p.name}>
                                                    {p.display_name} ({p.model_count} models)
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="API Key"
                                        type="password"
                                        value={apiKeyValue}
                                        onChange={(e) => setApiKeyValue(e.target.value)}
                                        fullWidth
                                        placeholder="Enter API key..."
                                    />
                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                        <Button onClick={() => {
                                            setShowAddKey(false);
                                            setSelectedProvider('');
                                            setApiKeyValue('');
                                        }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSaveApiKey}
                                            disabled={saving || !selectedProvider || !apiKeyValue}
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {/* Info Section */}
                        <Alert severity="info">
                            Organization API keys are shared by all members in 'basic' mode. 
                            Users in 'advanced' mode can override with personal API keys.
                        </Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
